import { Component, computed, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTable } from '@angular/material/table';
import { aggregateCatchBySpecies } from '../shared/catch-aggregation';
import { Catch } from '../shared/catch.model';
import { FishingEvent } from '../shared/fishing-event.model';
import { TrawlSet } from '../shared/trawl-set.model';
import { GroundfishDelivery } from '../shared/groundfish-delivery.model';
import { HMSDelivery } from '../shared/hms-delivery.model';
import { TrawlDelivery } from '../shared/trawl-delivery.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';


/**
 * A “year‑to‑date” (or arbitrary range) roll‑up of ALL catch:
 * ▸ sets still on the device  (pending trip)
 * ▸ sets already submitted, inside completed deliveries
 * Sets on drafts are not included.
 * The default range is Jan 1 → today; users can pick any dates.
 *
 */
@Component({
    selector: 'app-summary-report-ytd',
    templateUrl: './summary-report-ytd.component.html',
    styleUrls: ['./summary-report-ytd.component.css'],
    standalone: false
})
export class SummaryReportYtdComponent implements OnInit  {

  // template binds
  catchItems: Catch[] = []; // used for single fishery
  catchGFItems: Catch[] = []; //used for combo fishery
  catchHMSItems: Catch[] = [];
  displayedColumns = ['Species','RetainedCount','RetainedWeight','ReleasedCount','ReleasedWeight'];
  title = 'All Sets (YTD)';
  fishery!: 'Groundfish' | 'HMS' | 'Groundfish + HMS' | 'Trawl';

  completedGF: Signal<number>;
  completedHMS: Signal<number>;
  completedTrawl: Signal<number>;

  HMSDaysFished: number = 0;
  showCalendars = false;  


  start: WritableSignal<Date> = signal(new Date(new Date().getFullYear(), 0, 1));
  end: WritableSignal<Date> = signal(new Date());


  @ViewChild(MatTable) table!: MatTable<Catch>;

  constructor(private dataSync: LocalStorageService, private router: Router) {
    this.completedGF = computed(() => this.dataSync.groundfish_logbooks().filter(lb => lb.returnDate >= this.start() && lb.returnDate <= this.end()).length);
    this.completedHMS = computed(() => this.dataSync.hms_logbooks().filter(lb => lb.arrivalDate >= this.start() && lb.arrivalDate <= this.end()).length);
    this.completedTrawl = computed(() => this.dataSync.received_trawlLogbooks().filter(lb => lb.returnDate >= this.start() && lb.returnDate <= this.end()).length);
  }

  ngOnInit(): void {
    const user = this.dataSync.current_user();
    this.fishery = user.trawlFishery ? 'Trawl' : user.hmsFishery && user.groundfishFishery ? 'Groundfish + HMS' : user.hmsFishery ? 'HMS' : 'Groundfish';
    this.refreshTable();
  }

  /** Re‑build the table any time the range changes */
  refreshTable(): void {
    const start = this.start(), end = this.end();
    this.title = `All ${this.fishery} Catch`;
    if (this.fishery === 'Groundfish + HMS') {
      // build each one separately
      this.catchGFItems = this.buildAggregate('Groundfish', start, end);
      this.catchHMSItems = this.buildAggregate('HMS', start, end);
    } else {
      // single‑fishery path
      this.catchItems = this.buildAggregate(this.fishery as 'Groundfish' | 'HMS' | 'Trawl', start, end);
    }
    
    // Calculate days fished & completed trips for HMS
    if (this.fishery === 'HMS' || this.fishery === 'Groundfish + HMS') {
      this.HMSDaysFished = this.collectRelevantSetsFor('HMS').filter((s): s is FishingEvent => 'fishingMode' in s && s.fishingMode === 'Fish' && new Date(s.startDate) >= start && new Date(s.startDate) <= end).length;
    }

    // force table redraw
    if (this.table) this.table.renderRows();
}


  private buildAggregate(
    fishery: 'Groundfish'|'HMS' |'Trawl',
    start: Date, end: Date
  ): Catch[]
  {
  // 1) collect & date‑filter
  let allSets = this.collectRelevantSetsFor(fishery);
  if (fishery == 'Groundfish') 
    allSets = allSets.filter(s => !!s.endDate && new Date(s.endDate!) >= start && new Date(s.endDate!) <= end);
  else if (fishery == 'HMS')
    allSets = allSets.filter(s => new Date(s.startDate) >= start && new Date(s.startDate) <= end);

  // 2) aggregate by species -- HMS weights are per-fish averages and get multiplied by count first
  return aggregateCatchBySpecies(allSets);
  }

  /** Gathers:
   *   ▸ current‑trip sets
   *   ▸ sets in any completed deliveries (received_logbooks / received_trawlLogbooks)
   *   ▸ sets in any drafts (hms_drafts / trawl_drafts)
   */
private collectRelevantSetsFor(fishery: 'Groundfish' | 'HMS' | 'Trawl'): (FishingEvent | TrawlSet)[] {
  if (fishery === 'Trawl') {
    const pending: TrawlSet[] = this.dataSync.user_trawlTrip().sets;
    const completed: TrawlSet[] = this.dataSync.received_trawlLogbooks().map((d: TrawlDelivery) => d.sets).reduce((all, arr) => all.concat(arr), []);
    const drafts: TrawlSet[] = this.dataSync.trawl_drafts().map((d: TrawlDelivery) => d.sets).reduce((all, arr) => all.concat(arr), []);
    return [...pending, ...completed, ...drafts];
  }

  // pending 
  const pending = this.dataSync.user_trip().sets.filter(s => s.fishery === fishery);

  // completed
  const completed = (fishery === 'Groundfish' ? this.dataSync.groundfish_logbooks() : this.dataSync.hms_logbooks())
      .reduce((all, arr) => all.concat(arr.sets), [] as FishingEvent[]);

  // drafts (HMS only)
  const drafts = fishery === 'HMS' ? this.dataSync.hms_drafts().reduce((all, arr) => all.concat(arr.sets), [] as FishingEvent[]): [];

  return [...pending, ...completed, ...drafts];
}

  formatRange(s: Date, e: Date): string {
    const opts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day:   'numeric',
      year:  'numeric'
    };
    const fmt = (d: Date) => d.toLocaleDateString(undefined, opts);
    return `${fmt(s)} - ${fmt(e)}`;
  }

  /** Called when the user picks a new start date */
  onStartDate(date: Date) {
    this.start.set(date);
    this.refreshTable();
  }
  
  /** Called when the user picks a new end date */
  onEndDate(date: Date) {
    this.end.set(date);
    this.refreshTable();
  }

  navigateBack(): void {
    this.router.navigate(['menu']);
  }
}
